// ** Modules
import React, { useId } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

// ** Styles
import { DIN } from '@/styles/globalStyles';

// ** Utils
import { validateEmail } from '@/utils/shared/validation';

// ** Types
import type { Employee } from './EmployeeList.types';

type EmployeeFormValues = {
	name: string;
	firstName: string;
	email: string;
	username: string;
	hasTarifAccess: boolean;
};

type FieldErrors = Partial<Record<keyof EmployeeFormValues, string>>;

type EmployeeFormDialogProps = {
	open: boolean;
	onClose: () => void;
	onExited?: () => void;
	onSubmit: (values: EmployeeFormValues) => void;
	employee?: Employee | null;
};

const EMPTY_FORM: EmployeeFormValues = {
	name: '',
	firstName: '',
	email: '',
	username: '',
	hasTarifAccess: false
};

const inputSx = {
	'& .MuiOutlinedInput-root': {
		fontFamily: DIN,
		fontSize: '14px',
		borderRadius: '4px'
	},
	'& .MuiInputLabel-root': {
		fontFamily: DIN,
		fontSize: '14px'
	}
} as const;

function validate(values: EmployeeFormValues): FieldErrors {
	const errors: FieldErrors = {};

	if (!values.name.trim()) errors.name = 'Pflichtfeld';
	else if (values.name.trim().length < 2) errors.name = 'Mindestens 2 Zeichen';

	if (!values.firstName.trim()) errors.firstName = 'Pflichtfeld';
	else if (values.firstName.trim().length < 2) errors.firstName = 'Mindestens 2 Zeichen';

	if (!values.email.trim()) {
		errors.email = 'Pflichtfeld';
	} else if (values.email.trim().length < 2) {
		errors.email = 'Mindestens 2 Zeichen';
	} else if (!validateEmail(values.email)) {
		errors.email = 'Bitte überprüfen Sie Ihre Eingabe';
	}

	if (!values.username.trim()) errors.username = 'Pflichtfeld';
	else if (values.username.trim().length < 2) errors.username = 'Mindestens 2 Zeichen';

	return errors;
}

const EmployeeFormDialog = ({ open, onClose, onExited, onSubmit, employee }: EmployeeFormDialogProps) => {
	const isEdit = Boolean(employee);
	const titleId = useId();

	const [values, setValues] = React.useState<EmployeeFormValues>(EMPTY_FORM);
	const [errors, setErrors] = React.useState<FieldErrors>({});
	const [touched, setTouched] = React.useState<Partial<Record<keyof EmployeeFormValues, boolean>>>({});

	React.useEffect(() => {
		if (open) {
			if (employee) {
				setValues({
					name: employee.name,
					firstName: employee.firstName,
					email: employee.email,
					username: employee.username,
					hasTarifAccess: employee.hasTarifAccess
				});
			} else {
				setValues(EMPTY_FORM);
			}
			setErrors({});
			setTouched({});
		}
	}, [open, employee]);

	const handleChange = React.useCallback(
		(field: keyof EmployeeFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			setValues((prev) => ({ ...prev, [field]: val }));
			if (touched[field]) {
				setErrors((prev) => {
					const next = validate({ ...values, [field]: val });
					return { ...prev, [field]: next[field] };
				});
			}
		},
		[touched, values]
	);

	const handleBlur = React.useCallback(
		(field: keyof EmployeeFormValues) => () => {
			setTouched((prev) => ({ ...prev, [field]: true }));
			const fieldErrors = validate(values);
			setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }));
		},
		[values]
	);

	const handleRadioChange = React.useCallback((_: React.ChangeEvent<HTMLInputElement>, val: string) => {
		setValues((prev) => ({ ...prev, hasTarifAccess: val === 'ja' }));
	}, []);

	const isFormValid = React.useMemo(() => Object.keys(validate(values)).length === 0, [values]);

	const handleSubmit = React.useCallback(() => {
		setTouched({
			name: true,
			firstName: true,
			email: true,
			username: true
		});
		const fieldErrors = validate(values);
		setErrors(fieldErrors);

		if (Object.keys(fieldErrors).length === 0) {
			onSubmit(values);
			onClose();
		}
	}, [values, onSubmit, onClose]);

	const fieldProps = (field: keyof Omit<EmployeeFormValues, 'hasTarifAccess'>) =>
		({
			value: values[field],
			onChange: handleChange(field),
			onBlur: handleBlur(field),
			error: Boolean(touched[field] && errors[field]),
			helperText: touched[field] && errors[field] ? errors[field] : '\u00A0',
			size: 'small' as const,
			fullWidth: true,
			variant: 'outlined' as const,
			sx: {
				...inputSx,
				'& .MuiFormHelperText-root': {
					minHeight: '20px',
					marginTop: '2px'
				}
			}
		}) as const;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			aria-labelledby={titleId}
			slotProps={{
				transition: { onExited },
				paper: {
					sx: {
						width: { xs: '100%', sm: 640 },
						maxWidth: '100%',
						minHeight: 'auto',
						borderRadius: '8px',
						bgcolor: 'common.white',
						pt: { xs: 6, sm: 8 },
						pb: { xs: 6, sm: 8 },
						pl: { xs: 6, sm: 8 },
						pr: { xs: 6, sm: 6 },
						m: { xs: 4, sm: 8 },
						boxSizing: 'border-box',
						position: 'relative'
					}
				},
				backdrop: {
					sx: { bgcolor: 'rgba(0,0,0,0.55)' }
				}
			}}
			maxWidth={false}
		>
			<IconButton
				onClick={onClose}
				aria-label="Schließen"
				sx={{
					position: 'absolute',
					top: { xs: 12, sm: 16 },
					right: { xs: 12, sm: 16 },
					color: 'text.primary'
				}}
			>
				<CloseIcon fontSize="small" />
			</IconButton>

			<Typography id={titleId} variant="title-sm" component="h2" sx={{ color: 'common.black', mb: 6, pr: 8 }}>
				{isEdit ? 'Mitarbeiter-Zugang bearbeiten' : 'Neuen Mitarbeiter-Zugang anlegen'}
			</Typography>

			<Box
				component="form"
				noValidate
				onSubmit={(e: React.FormEvent) => {
					e.preventDefault();
					handleSubmit();
				}}
				sx={{
					display: 'flex',
					flexDirection: 'column'
				}}
			>
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
						gap: { xs: 2, sm: 4 },
						mb: 4
					}}
				>
					<TextField label="Vorname" required {...fieldProps('firstName')} />
					<TextField label="Name" required {...fieldProps('name')} />
					<TextField label="E-Mail" required type="email" autoComplete="email" {...fieldProps('email')} />
					<TextField label="Username" required {...fieldProps('username')} />
				</Box>

				<FormControl component="fieldset" sx={{ mb: 6 }}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 4
						}}
					>
						<FormLabel
							component="legend"
							sx={{
								color: 'common.black',
								fontWeight: 500,
								'&.Mui-focused': { color: 'common.black' }
							}}
						>
							<Typography variant="body-md" component="span" sx={{ color: 'inherit', fontWeight: 'inherit' }}>
								Tarif-Einsicht
							</Typography>
						</FormLabel>
						<RadioGroup row value={values.hasTarifAccess ? 'ja' : 'nein'} onChange={handleRadioChange}>
							<FormControlLabel
								value="ja"
								control={<Radio size="small" />}
								label={
									<Typography variant="body-sm" sx={{ color: 'text.primary' }}>
										Ja
									</Typography>
								}
							/>
							<FormControlLabel
								value="nein"
								control={<Radio size="small" />}
								label={
									<Typography variant="body-sm" sx={{ color: 'text.primary' }}>
										Nein
									</Typography>
								}
							/>
						</RadioGroup>
					</Box>
				</FormControl>

				<Button
					type="submit"
					variant="contained"
					disabled={!isFormValid}
					disableElevation
					sx={{
						alignSelf: 'flex-start',
						bgcolor: 'loginOverlayCta.main',
						color: 'common.white',
						fontFamily: DIN,
						fontWeight: 500,
						fontSize: '14px',
						textTransform: 'none',
						borderRadius: '4px',
						px: 8,
						py: 2.5,
						'&:hover': { bgcolor: 'loginOverlayCta.dark' },
						'&.Mui-disabled': {
							bgcolor: 'action.disabledBackground',
							color: 'text.disabled'
						}
					}}
				>
					{isEdit ? 'Speichern' : 'Anlegen'}
				</Button>
			</Box>
		</Dialog>
	);
};

export default EmployeeFormDialog;
